function fuzzyMatch(query: string, text: string): boolean {
  let qi = 0
  for (let i = 0; i < text.length && qi < query.length; i++) {
    if (text[i] === query[qi]) qi++
  }
  return qi === query.length
}

export default function init() {
  new DistrictSelectMenu()
}

const MENU_MODE = {
  LOCATION: 'location',
  DISTRICT: 'district'
} as const

type MenuMode = (typeof MENU_MODE)[keyof typeof MENU_MODE]

class DistrictSelectMenu {
  private menu_type: MenuMode = MENU_MODE.LOCATION

  private drawer = document.querySelector(
    '[data-district-select-drawer]'
  ) as HTMLElement | null

  private toggle_button = this.drawer?.querySelector(
    '[data-menu-type-toggle]'
  ) as HTMLButtonElement | null

  private heading = this.drawer?.querySelector(
    '#DistrictSelectMenuHeading'
  ) as HTMLHeadingElement | null

  private input = this.drawer?.querySelector(
    '[data-district-select-input]'
  ) as HTMLInputElement | null

  private no_results = this.drawer?.querySelector(
    '[data-district-no-results]'
  ) as HTMLElement | null

  constructor() {
    this.setModeAttribute()
    this.toggle_button?.addEventListener('click', () => this.toggleMenuType())
    this.input?.addEventListener('input', () => this.filterItems())
  }

  private toggleMenuType() {
    if (!this.toggle_button) return

    this.menu_type =
      this.menu_type === MENU_MODE.LOCATION
        ? MENU_MODE.DISTRICT
        : MENU_MODE.LOCATION

    this.setModeAttribute()
    this.updateHeading()
    this.updateToggleLabel()

    // Clear search and reset visibility when switching modes
    if (this.input) this.input.value = ''
    this.filterItems()
  }

  private setModeAttribute() {
    this.drawer?.setAttribute('data-current-menu-type', this.menu_type)
  }

  private updateHeading() {
    if (!this.heading) return
    const attr =
      this.menu_type === MENU_MODE.LOCATION
        ? 'data-location-mode-heading'
        : 'data-district-mode-heading'
    this.heading.textContent = this.heading.getAttribute(attr) ?? ''
  }

  private updateToggleLabel() {
    if (!this.toggle_button) return
    const attr =
      this.menu_type === MENU_MODE.LOCATION
        ? 'data-location-mode-label'
        : 'data-district-mode-label'
    this.toggle_button.textContent = this.toggle_button.getAttribute(attr) ?? ''
  }

  private filterItems() {
    const query = this.input?.value.trim().toLowerCase() ?? ''
    const active_content = this.drawer?.querySelector(
      `[data-menu-type-content="${this.menu_type}"]`
    )
    if (!active_content) return

    let any_visible = false

    if (this.menu_type === MENU_MODE.LOCATION) {
      // Match region groups directly as grid items
      active_content
        .querySelectorAll<HTMLElement>('[data-ui-expandable-content]')
        .forEach((group) => {
          const children =
            group.querySelectorAll<HTMLElement>('.DistrictListItem')
          let group_has_match = false

          children.forEach((child) => {
            const matches =
              query.length === 0 ||
              fuzzyMatch(query, child.textContent?.toLowerCase() ?? '')
            child.style.display = matches ? '' : 'none'
            if (matches) group_has_match = true
          })

          group.style.display = group_has_match ? '' : 'none'
          if (group_has_match) any_visible = true
        })
    } else {
      // Distrikt med avdelningar under sig (#314): ett distrikt visas om det
      // självt matchar (då syns alla avdelningar) eller om någon avdelning gör det.
      active_content
        .querySelectorAll<HTMLElement>('.DistrictSelectItem')
        .forEach((district) => {
          const district_link = district.querySelector(':scope > a')
          const district_matches =
            query.length === 0 ||
            fuzzyMatch(query, district_link?.textContent?.toLowerCase() ?? '')
          const children =
            district.querySelectorAll<HTMLElement>('.DistrictListItem')
          let child_has_match = false

          children.forEach((child) => {
            const matches =
              district_matches ||
              fuzzyMatch(query, child.textContent?.toLowerCase() ?? '')
            child.style.display = matches ? '' : 'none'
            if (matches) child_has_match = true
          })

          const visible = district_matches || child_has_match
          district.style.display = visible ? '' : 'none'
          if (visible) any_visible = true
        })
    }

    if (this.no_results) {
      this.no_results.hidden = query.length === 0 || any_visible
    }
  }
}
